import styled from '@emotion/styled';
import { ModulesStates } from '@racemap/utilities/consts/events';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import { NotActivatedOverlay } from 'src/components/BasicComponents/NotActivatedOverlay/NotActivatedOverlay';
import { LeaderboardHeader } from 'src/components/Leaderboard/Header';
import { LeaderboardFooter } from 'src/components/Leaderboard/LeaderboardFooter';
import { checkLeaderboardActivationState } from 'src/components/Leaderboard/utils';
import { useScrollLock } from 'src/lib/customHooks/useScrollLock';
import {
  useIsFirstLoad,
  useIsLoading,
  useLoadError,
  useParentEventData,
  useRelevantEventsData,
  useSelectedEventData,
  useSelectedEventId,
} from 'src/store/leaderboard/leaderboard_reducer';
import { LeaderboardTableAndSponsorLogos } from '../components/Leaderboard/Leaderboard/LeaderboardTableAndSponsorLogos.js';
import { LeaderboardErrorPage } from './LeaderboardErrorPage';

type OuterLeaderBoardProps = {
  design?: 'standard' | 'slim' | 'compact';
  containerHeight?: number | string;
  onSelectedEventChange?: (newEventId: string, newSlug?: string) => void;
};

export function OuterLeaderBoard({
  design = 'standard',
  containerHeight = '100%',
  onSelectedEventChange,
}: OuterLeaderBoardProps) {
  const relevantEvents = useRelevantEventsData();
  const selectedEventId = useSelectedEventId();
  const loading = useIsLoading();
  const isFirstLoad = useIsFirstLoad();
  const loadError = useLoadError();
  const selectedEventData = useSelectedEventData();
  const parentEventData = useParentEventData();
  const selectedEvent = selectedEventData?.eventObject;
  const parentEvent = parentEventData?.eventObject;
  const { isArchived, isModuleActivated, isEventAuthorized } = checkLeaderboardActivationState(
    selectedEvent || null,
  );

  useScrollLock();

  return (
    <Container style={{ height: containerHeight }}>
      <LeaderboardHeader
        events={Array.from(relevantEvents.values())
          .map((e) => e.eventObject)
          // the group is the overview, whatever its own state; the selected event keeps its header
          .filter(
            (e) =>
              isGroupEvent(e) ||
              e.modules.timing.state === ModulesStates.ACTIVATED ||
              e.id === selectedEventId,
          )}
        selectedEventId={selectedEventId}
        parentEvent={parentEvent}
        onSelectedEventChange={onSelectedEventChange}
        loading={loading}
      />
      {!loadError && (isFirstLoad || (!isArchived && isModuleActivated)) ? (
        <>
          <LeaderBoardContainer design={design}>
            <LeaderboardTableAndSponsorLogos />
          </LeaderBoardContainer>
          {design !== 'compact' && !isFirstLoad && <LeaderboardFooter />}
        </>
      ) : (
        <LeaderboardErrorPage event={selectedEvent} loadError={loadError} />
      )}

      {!loadError && !isEventAuthorized && !isArchived && isModuleActivated && (
        <NotActivatedOverlay
          className={TestSelectors.leaderboard.mapNotActivatedOverlay.toString()}
        />
      )}
    </Container>
  );
}

const Container = styled.div<{ height?: number | string }>`
  height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height || '100%')};
  display: flex;
  flex-direction: column;
`;

const LeaderBoardContainer = styled.div<{ design: 'standard' | 'slim' | 'compact' }>`
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px;

  @media screen and (min-width: 576px) {
    padding-left: 30px;
    padding-right: 30px;
  }

  @media screen and (min-height: 800px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  ${({ design }) => {
    if (design === 'compact') {
      return 'padding: 0 !important;';
    }
    if (design === 'slim') {
      return 'padding: 10px !important;';
    }
  }}
`;
